import { inject, injectable } from 'inversify'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { ILine, IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import type { BaseEntity, IEntity } from '@/plugins/cad/module/CADModule/model/entity/BaseEntity'
import type { EntityDetector } from '@/plugins/cad/module/CADModule/selector/entityDetector'
import { getPixelSizeInWorld } from '@/plugins/cad/module/common/utils'
import { getActiveViewSize } from '@/plugins/cad/module/common/view'
import { TYPES } from '@/plugins/cad/module/container/types'
import { CaptureMarker } from '@/plugins/cad/module/core/auxiliaryTool/CaptureMarker'
import {
  Capturer,
  CaptureType,
  type ICaptureResult,
} from '@/plugins/cad/module/core/auxiliaryTool/Capturer'
import type { BaseTool } from '@/plugins/cad/module/core/tool/BaseTool'
import type { EngineContext } from '@/runtime/EngineContext'

/** 捕捉数据（包含点和线） */
export interface ICaptureSourceData {
  /** 端点捕捉候选点集 */
  endPoints?: IPoint[]
  /** 中点捕捉候选点集 */
  midPoints?: IPoint[]
  /** 节点捕捉候选点集 */
  nodePoints?: IPoint[]
  /** 圆心捕捉候选点集 */
  centerPoints?: IPoint[]
  /** 最近点捕捉参考线段集 */
  lines?: ILine[]
}

/**
 * 额外捕捉请求数据：除静态点/线外，可提供额外候选实体。
 */
export interface ICaptureReqExtraData extends ICaptureSourceData {
  /** 额外参与捕捉检测的实体列表 */
  entities?: IEntity[]
}

/**
 * CAD 点捕捉辅助器，统一收集实体及外部候选数据，挑选距离鼠标最近的捕捉点。
 */
@injectable()
export class CaptureHelper extends Capturer<IEntity> {
  @inject(TYPES.EntityDetector)
  private readonly entityDetector!: EntityDetector

  @inject(TYPES.EngineContext)
  private readonly ctx!: EngineContext

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  private _captureMarker: CaptureMarker | null = null

  // 圆心捕捉实体类型集合
  private readonly centerCaptureEntityTypes = new Set([
    EntityType.Circle,
    EntityType.Arc,
    EntityType.Ellipse,
  ])

  private get captureMarker(): CaptureMarker {
    if (!this._captureMarker) {
      this._captureMarker = new CaptureMarker()
      this.addCaptureMarkerToGroup()
    }
    return this._captureMarker
  }

  /**
   * 捕捉点。
   *
   * @param mousePoint - 鼠标屏幕位置
   * @param worldPoint - 鼠标对应的世界坐标
   * @param entities - 参与捕捉的实体列表，缺省时自动检测命中实体
   * @param extraData - 额外的捕捉候选点与实体
   * @returns 捕捉结果，未命中时返回 null
   */
  capture(
    mousePoint: IPoint,
    worldPoint: IPoint,
    entities?: IEntity[] | undefined,
    extraData?: ICaptureReqExtraData
  ): ICaptureResult<IEntity> | null {
    const result = this.handleCapture(mousePoint, worldPoint, entities, extraData)
    if (result) {
      this.config.showMarker && this.updateCaptureMarkerByResult(result)
    } else {
      this.clearCaptureMarker()
    }
    this.lastCaptureResult = result
    return result
  }

  private handleCapture(
    mousePoint: IPoint,
    worldPoint: IPoint,
    entities?: IEntity[],
    extraData?: ICaptureReqExtraData
  ): ICaptureResult<IEntity> | null {
    if (!this.config.enabled) {
      return null
    }

    // 如果没有传入实体列表，则使用检测到的实体
    const entitiesToCapture = entities || this.getCaptureEntities(mousePoint)
    if (extraData?.entities) {
      entitiesToCapture.push(...extraData.entities)
    }
    // 是否需要搜索圆心捕捉点
    const shouldSearchCenterPoints = !entities && this.isCaptureTypeEnabled(CaptureType.Center)
    if (entitiesToCapture.length === 0 && !extraData && !shouldSearchCenterPoints) {
      return null
    }

    // 获取最近的候选捕捉点
    let candidatePoint:
      | {
          point: IPoint
          entity?: IEntity
          distance: number
          type: CaptureType
        }
      | undefined

    // 待处理的捕捉线
    const lines: ILine[] = []
    // 捕捉距离(初始为配置的捕捉距离)
    let minPointDistance = this.getCaptureDistance()
    // 辅助函数：处理单个捕捉点
    const handlePoint = (point: IPoint, type: CaptureType, entity?: IEntity) => {
      // 计算到鼠标世界坐标的距离
      const distance = this.calculateDistance(point, worldPoint)
      if (distance <= minPointDistance) {
        minPointDistance = distance
        candidatePoint = {
          point: { ...point },
          entity,
          distance,
          type,
        }
      }
    }
    const handleCaptureSourceData = (captureData: ICaptureSourceData, entity?: IEntity) => {
      lines.push(...(captureData.lines || []))

      // 处理捕捉点
      // 端点捕捉
      if (this.isCaptureTypeEnabled(CaptureType.Endpoint)) {
        for (const point of captureData.endPoints || []) {
          handlePoint(point, CaptureType.Endpoint, entity)
        }
      }
      // 中点捕捉
      if (this.isCaptureTypeEnabled(CaptureType.Midpoint)) {
        for (const point of captureData.midPoints || []) {
          handlePoint(point, CaptureType.Midpoint, entity)
        }
      }
      // 节点捕捉
      if (this.isCaptureTypeEnabled(CaptureType.Node)) {
        for (const point of captureData.nodePoints || []) {
          handlePoint(point, CaptureType.Node, entity)
        }
      }
      // 圆心捕捉
      if (this.isCaptureTypeEnabled(CaptureType.Center)) {
        for (const point of captureData.centerPoints || []) {
          handlePoint(point, CaptureType.Center, entity)
        }
      }
    }

    for (const entity of entitiesToCapture) {
      const captureData = (entity as BaseEntity).getCaptureData()
      handleCaptureSourceData(captureData, entity)
    }

    // 处理额外的捕捉数据
    if (extraData) {
      handleCaptureSourceData(extraData)
    }

    // 圆心不在圆周几何线上，鼠标靠近圆心时射线不会命中圆/弧；这里只补充扫描有圆心的实体。
    if (!candidatePoint && shouldSearchCenterPoints) {
      const handledEntityIds = new Set(entitiesToCapture.map((entity) => entity.id))
      for (const entity of this.getCenterCaptureEntities(handledEntityIds)) {
        const captureData = (entity as BaseEntity).getCaptureData()
        for (const point of captureData.centerPoints || []) {
          handlePoint(point, CaptureType.Center, entity)
        }
      }
    }

    // 优先处理捕捉点
    let result = candidatePoint || null
    if (result) {
      return result
    }

    // 若以上没有捕捉点，处理捕捉线的最近点
    if (this.isCaptureTypeEnabled(CaptureType.Nearest)) {
      for (const line of lines) {
        const nearestPoint = this.findNearestPointOnLine(worldPoint, line)
        handlePoint(nearestPoint, CaptureType.Nearest)
      }
    }

    // 根据候选点生成捕捉结果
    result = candidatePoint || null
    return result
  }

  private getCaptureDistance() {
    const radius = this.config.captureRadius
    const camera = this.ctx.getService('ViewportCamera').getActiveCamera()
    return getPixelSizeInWorld(camera, getActiveViewSize(this.ctx), radius)
  }

  /**
   * 获取所有捕捉实体
   * @param mousePoint 鼠标位置（屏幕坐标）
   * @returns 捕捉实体数组
   */
  private getCaptureEntities(mousePoint: IPoint): IEntity[] {
    const entities = this.entityDetector.handleDetect(mousePoint)
    return entities
  }

  private getCenterCaptureEntities(excludedEntityIds: Set<string>): IEntity[] {
    return this.viewManager.dataManager.entities.filter(
      (entity) =>
        !excludedEntityIds.has(entity.id) && this.centerCaptureEntityTypes.has(entity.type)
    )
  }

  /**
   * 接入当前工具，同时将实体探测器注入其依赖链。
   *
   * @param tool - 当前激活的工具实例
   */
  injectTool(tool: BaseTool): void {
    super.injectTool(tool)
    tool.inject(this.entityDetector)
  }

  /** 与当前工具解绑，清空捕捉标记并解除实体探测器注入 */
  ejectTool(): void {
    this.clear()
    this.tool?.eject(this.entityDetector)
    super.ejectTool()
  }

  /** 绘制捕捉辅助图形 */
  private updateCaptureMarkerByResult(result: ICaptureResult<IEntity>) {
    this.captureMarker.update(result.point, result.type)
    this.addCaptureMarkerToGroup()
  }

  /** 清除捕捉辅助图形 */
  private clearCaptureMarker() {
    this.captureMarker.update({ x: 0, y: 0 }, CaptureType.None)
  }

  /** 清除捕捉标记 */
  clear() {
    this.clearCaptureMarker()
  }

  private addCaptureMarkerToGroup() {
    const marker = this._captureMarker
    const group = this.viewManager.tempGroup
    if (marker && !marker.isAddedToGroup() && group) {
      marker.addToGroup(group)
    }
  }

  /** 释放捕捉标记占用的图形资源 */
  dispose() {
    this._captureMarker?.dispose()
    this._captureMarker = null
  }
}
